import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, TextField, Snackbar, Skeleton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchMyReferral, Referral } from '../../services/monetizationServices';

/**
 * Share and earn (#09).
 *
 * Renders nothing at all when referrals are unavailable — no code, no account, or
 * the endpoint absent on this deployment. A broken share panel on a student's
 * dashboard is worse than no panel: it invites them to send a code that will not
 * work, and the person they send it to is the one who finds out.
 */
const ReferralPanel: React.FC = () => {
  const [referral, setReferral] = useState<Referral | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await fetchMyReferral();
      if (cancelled) return;
      if (result.state === 'ready' && result.referral.active) setReferral(result.referral);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Skeleton variant="rounded" height={140} sx={{ my: 2 }} />;
  if (!referral) return null;

  const shareUrl = `${window.location.origin}/login?ref=${encodeURIComponent(referral.code)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
    } catch {
      // Clipboard is blocked in some embedded browsers. The field below is
      // selectable, so there is still a way to share; just do not claim success.
    }
  };

  return (
    <Card sx={{ my: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Share &amp; earn</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Invite a friend with your code. When they join, you both get a reward.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            value={referral.code}
            label="Your code"
            size="small"
            InputProps={{ readOnly: true }}
            sx={{ width: 160, '& input': { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1 } }}
          />
          <TextField
            value={shareUrl}
            label="Share link"
            size="small"
            InputProps={{ readOnly: true }}
            sx={{ flex: '1 1 240px' }}
          />
          <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copy}>
            Copy
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {referral.redemptions === 0
            ? 'No one has used your code yet.'
            : `${referral.redemptions} ${referral.redemptions === 1 ? 'person has' : 'people have'} used your code.`}
        </Typography>
      </CardContent>

      <Snackbar
        open={copied}
        autoHideDuration={2500}
        onClose={() => setCopied(false)}
        message="Share link copied"
      />
    </Card>
  );
};

export default ReferralPanel;
